import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SentimentDissatisfiedRoundedIcon from '@mui/icons-material/SentimentDissatisfiedRounded';
import { Component, type ErrorInfo, type PropsWithChildren, type ReactNode } from 'react';
import { reportClientError } from './api';
import { useLanguage } from './i18n';

let installed = false;

export function installClientErrorReporting(): void {
  if (installed) return;
  installed = true;
  window.addEventListener('error', (event) => {
    reportClientError({
      message: event.message || 'Unhandled browser error',
      ...(event.error instanceof Error && event.error.stack ? { stack: event.error.stack } : {}),
      source: event.filename || 'window.error',
      line: event.lineno,
      column: event.colno,
      path: window.location.pathname,
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportClientError({
      message: reason instanceof Error ? reason.message : String(reason || 'Unhandled promise rejection'),
      ...(reason instanceof Error && reason.stack ? { stack: reason.stack } : {}),
      source: 'unhandledrejection',
      path: window.location.pathname,
    });
  });
}

export class ClientErrorBoundary extends Component<PropsWithChildren, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientError({
      message: error.message,
      stack: [error.stack, info.componentStack].filter(Boolean).join('\n'),
      source: 'React error boundary',
      path: window.location.pathname,
    });
  }

  render(): ReactNode {
    if (this.state.failed) {
      return <ClientErrorFallback />;
    }
    return this.props.children;
  }
}

function ClientErrorFallback() {
  const { text } = useLanguage();

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        py: 7,
        background:
          'radial-gradient(circle at 50% 15%, rgba(121,223,182,.16), transparent 28rem), #07111b',
      }}
    >
      <Container maxWidth="sm">
        <Paper sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
          <Stack spacing={2.5} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgba(255,184,107,.14)',
                color: 'secondary.main',
              }}
            >
              <SentimentDissatisfiedRoundedIcon sx={{ fontSize: 34 }} />
            </Box>
            <Box>
              <Typography variant="overline" sx={{ color: 'secondary.main', fontWeight: 800 }}>
                {text('Page unavailable', 'Seite nicht verfügbar')}
              </Typography>
              <Typography variant="h3" sx={{ mt: 0.5, fontSize: { xs: '2rem', sm: '2.6rem' } }}>
                {text('Something went wrong.', 'Da ist etwas schiefgelaufen.')}
              </Typography>
            </Box>
            <Typography sx={{ color: 'text.secondary', maxWidth: 440 }}>
              {text(
                'We could not display this page. The error was recorded automatically.',
                'Diese Seite konnte nicht angezeigt werden. Der Fehler wurde automatisch protokolliert.',
              )}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<RefreshRoundedIcon />}
                onClick={() => window.location.reload()}
              >
                {text('Try again', 'Erneut versuchen')}
              </Button>
              <Button
                component="a"
                href="/"
                variant="outlined"
                color="inherit"
                size="large"
                startIcon={<HomeRoundedIcon />}
              >
                {text('Back to start', 'Zur Startseite')}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
